import { Component, OnDestroy, OnInit } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { NbToastrService } from '@nebular/theme';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { NUMBERS_PATTERN } from '../../../@auth/components';
import { Subject } from 'rxjs/Subject';
import {
  SubscriptionPlan,
  SubscriptionPlanData,
} from '../../../@core/interfaces/common/CalorieFriend/subscriptionPlan';

enum SubscriptionMode {
  EDIT = 'Edit',
  ADD = 'Add',
}

@Component({
  selector: 'ngx-subscription',
  templateUrl: './subscription.component.html',
  styleUrls: ['./subscription.component.scss'],
})
export class SubscriptionComponent implements OnInit, OnDestroy {
  subscriptionForm: FormGroup;

  mode: SubscriptionMode;
  subscription: SubscriptionPlan;

  protected readonly unsubscribe$ = new Subject<void>();

  get planName() {
    return this.subscriptionForm.get('planName');
  }

  get planCode() {
    return this.subscriptionForm.get('planCode');
  }

  get priceId() {
    return this.subscriptionForm.get('priceId');
  }

  get planType() {
    return this.subscriptionForm.get('planType');
  }

  get price() {
    return this.subscriptionForm.get('price');
  }

  get patientsCount() {
    return this.subscriptionForm.get('patientsCount');
  }

  get isFreePlan() {
    return this.subscriptionForm.get('isFreePlan');
  }

  get hasAssessments() {
    return this.subscriptionForm.get('hasAssessments');
  }

  get hasBranding() {
    return this.subscriptionForm.get('hasBranding');
  }

  get hasPlanChoices() {
    return this.subscriptionForm.get('hasPlanChoices');
  }

  constructor(
    private subscriptionsService: SubscriptionPlanData,
    private router: Router,
    private route: ActivatedRoute,
    private toastrService: NbToastrService,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.initsubscriptionForm();
    const id = this.route.snapshot.paramMap.get('id');
    this.loadSubscriptionData(id);
    this.initsubscriptionsData();

    if (this.subscription.id) {
      this.setViewMode(SubscriptionMode.EDIT);
    } else {
      this.setViewMode(SubscriptionMode.ADD);
    }
  }
  initsubscriptionsData() {
    const subscriptionData = this.route.snapshot.data.subscriptionData;
    this.subscription = subscriptionData.subscription;
  }

  loadSubscriptionData(id) {
    if (id) {
      this.subscriptionsService
        .get(id)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((subscription) => {
          this.subscriptionForm.setValue({
            id: subscription.id,
            planName: subscription.planName ? subscription.planName : '',
            planCode: subscription.planCode ? subscription.planCode : '',
            priceId: subscription.priceId ? subscription.priceId : '',
            planType: subscription.planType ? subscription.planType : 0,
            price: subscription.price ? subscription.price : 0,
            patientsCount: subscription.patientsCount
              ? subscription.patientsCount
              : 0,
            isFreePlan: subscription.isFreePlan,
            hasAssessments: subscription.hasAssessments,
            hasBranding: subscription.hasBranding,
            hasPlanChoices: subscription.hasPlanChoices,
          });
          // this is a place for value changes handling
          // this.userForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {   });
        });
    }
  }

  initsubscriptionForm() {
    this.subscriptionForm = this.fb.group({
      id: this.fb.control(undefined),
      planName: this.fb.control('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(20),
      ]),
      planCode: this.fb.control('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(20),
      ]),
      priceId: this.fb.control('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(40),
      ]),
      planType: this.fb.control(0, [Validators.required]),
      patientsCount: this.fb.control(0, [
        Validators.required,
        Validators.min(-1),
        Validators.max(9999),
        Validators.pattern(NUMBERS_PATTERN),
      ]),
      price: this.fb.control(0, [
        Validators.required,
        Validators.min(0),
        Validators.pattern(NUMBERS_PATTERN),
      ]),
      isFreePlan: this.fb.control(0, [Validators.required]),
      hasAssessments: this.fb.control(0, [Validators.required]),
      hasBranding: this.fb.control(0, [Validators.required]),
      hasPlanChoices: this.fb.control(0, [Validators.required]),
    });
  }

  convertToSubscription(value: any): SubscriptionPlan {
    const subscription: SubscriptionPlan = value;
    return subscription;
  }

  save() {
    this.subscription = this.convertToSubscription(this.subscriptionForm.value);

    const observable = this.subscription.id
      ? this.subscriptionsService.update(this.subscription)
      : this.subscriptionsService.create(this.subscription);

    observable.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.toastrService.success(
        '',
        `Subscription Plan ${
          this.subscription.planName ? 'updated' : 'created'
        }!`,
      );
      this.back();
    });
  }

  back() {
    this.router.navigate(['/admin/subscriptions/list']);
  }

  setViewMode(viewMode: SubscriptionMode) {
    this.mode = viewMode;
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
